import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import billingApi from '../services/billingApi';
import { normalizePlanAmount } from '../services/planUpgradeBanner';
import {
  CreditCard, Landmark, Store, Lock, Loader2, AlertCircle,
  CheckCircle, Clock, ArrowLeft
} from 'lucide-react';

type PaymentMethod = 'card' | 'spei' | 'oxxo';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const goBack = useSmartBack('/');
  const plan = searchParams.get('plan') || 'home_plus';
  const condoId = searchParams.get('condoId') || undefined;

  const [method, setMethod] = useState<PaymentMethod>('card');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planInfo, setPlanInfo] = useState<any>(null);
  const [checkoutResult, setCheckoutResult] = useState<any>(null);

  useEffect(() => {
    // Fetch plan details
    billingApi.getPlans(condoId ? undefined : undefined)
      .then((data) => {
        const found = data.plans?.find((p: any) => p.id === plan);
        if (found) setPlanInfo(found); else setError('El plan solicitado no está disponible.');
      })
      .catch(() => setError('No se pudieron cargar los planes disponibles.'))
      .finally(() => setLoading(false));
  }, [plan, condoId]);

  const amountUSD = normalizePlanAmount(planInfo?.computed_usd) ?? 0;

  const handlePay = async () => {
    setProcessing(true);
    setError(null);
    try {
      const result = await billingApi.checkout(plan, condoId, method);
      setCheckoutResult(result);

      // For card payments, redirect to hosted checkout
      if (method === 'card' && result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      // For SPEI/OXXO, show the reference inline (no redirect)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Error al procesar el pago. Intenta de nuevo.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-[var(--v-surface-2)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-700)]" />
      </div>
    );
  }

  // Show SPEI/OXXO result after checkout
  if (checkoutResult && (method === 'spei' || method === 'oxxo')) {
    return (
      <PendingPayment
        method={method}
        result={checkoutResult}
        amountUSD={amountUSD}
        planName={planInfo?.name || 'Home+'}
      />
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-sm text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <PageHeader
          title="Finalizar pago"
          subtitle={planInfo?.name || 'Home+'}
        />

        {error && (
          <div className="mb-6 p-4 rounded-[var(--v-radius-md)] bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Order Summary */}
        <Card className="mb-6 p-6">
          <h3 className="font-bold mb-4">Resumen del pedido</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--v-text-secondary)]">Plan</span>
              <span className="font-medium">{planInfo?.name || 'Home+'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--v-text-secondary)]">Ciclo de facturación</span>
              <span className="font-medium">Mensual</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--v-text-secondary)]">Monto (USD)</span>
              <span className="font-medium">${amountUSD.toFixed(2)} USD</span>
            </div>
            <div className="border-t border-[var(--v-border-light)] pt-2 mt-2 flex justify-between">
              <span className="font-bold">Total a pagar</span>
              <span className="font-bold text-[var(--v-teal-700)]">${amountUSD.toFixed(2)} USD</span>
            </div>
          </div>
        </Card>

        {/* Payment Method Selector */}
        <Card className="mb-6 p-6">
          <h3 className="font-bold mb-4">Método de pago</h3>
          <div className="space-y-3">
            <MethodOption
              icon={<CreditCard className="w-5 h-5" />}
              label="Tarjeta de crédito/débito"
              description="Visa, Mastercard, American Express"
              selected={method === 'card'}
              onClick={() => setMethod('card')}
            />
            <MethodOption
              icon={<Landmark className="w-5 h-5" />}
              label="SPEI (Transferencia bancaria)"
              description="Transferencia desde tu banco"
              selected={method === 'spei'}
              onClick={() => setMethod('spei')}
            />
            <MethodOption
              icon={<Store className="w-5 h-5" />}
              label="OXXO"
              description="Pago en efectivo en tiendas OXXO"
              selected={method === 'oxxo'}
              onClick={() => setMethod('oxxo')}
            />
          </div>
        </Card>

        {/* Trust Signals */}
        <div className="flex items-center justify-center gap-2 mb-6 text-sm text-[var(--v-text-secondary)]">
          <Lock className="w-4 h-4 text-[var(--v-success)]" />
          Pago seguro procesado por Openpay
        </div>

        {/* Pay Button */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={handlePay}
          disabled={processing || !planInfo || !!error}
        >
          {processing ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Procesando...</>
          ) : method === 'card' ? (
            <>Pagar ${amountUSD.toFixed(2)} USD</>
          ) : method === 'spei' ? (
            <>Generar referencia SPEI</>
          ) : (
            <>Generar ficha de pago OXXO</>
          )}
        </Button>

        <p className="text-center text-xs text-[var(--v-text-secondary)] mt-4">
          <>Al continuar, aceptas los <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Términos y Condiciones</a> y la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Política de Privacidad</a> de Vecindus.</>
        </p>
      </div>
    </div>
  );
}

function MethodOption({ icon, label, description, selected, onClick }: {
  icon: React.ReactNode;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-4 p-4 rounded-[var(--v-radius-md)] border-2 transition-all text-left ${
        selected
          ? 'border-[var(--v-teal-700)] bg-[var(--v-teal-50)]'
          : 'border-[var(--v-border-light)] hover:border-[var(--v-border-strong)]'
      }`}
    >
      <div className={`flex-shrink-0 ${selected ? 'text-[var(--v-teal-700)]' : 'text-[var(--v-text-secondary)]'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="font-medium">{label}</div>
        <div className="text-sm text-[var(--v-text-secondary)]">{description}</div>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${
        selected ? 'border-[var(--v-teal-700)] bg-[var(--v-teal-700)]' : 'border-[var(--v-border-strong)]'
      }`}>
        {selected && <CheckCircle className="w-full h-full text-white p-0.5" />}
      </div>
    </button>
  );
}

function PendingPayment({ method, result, amountUSD, planName }: {
  method: PaymentMethod;
  result: any;
  amountUSD: number;
  planName: string;
}) {
  const reference = result.payment_reference || result.provider_ref || '—';
  const expiresAt = result.expires_at;

  return (
    <div className="min-h-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <PageHeader title="Pago pendiente" subtitle="Sigue estas instrucciones para completar tu pago" />

        <Card className="p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Pago en proceso</h3>
              <p className="text-sm text-[var(--v-text-secondary)]">
                {method === 'spei' ? 'Realiza la transferencia bancaria' : 'Acude a tu tienda OXXO más cercana'}
              </p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between p-3 bg-[var(--v-surface-2)] rounded-[var(--v-radius-sm)]">
              <span className="text-[var(--v-text-secondary)]">Plan</span>
              <span className="font-medium">{planName}</span>
            </div>
            <div className="flex justify-between p-3 bg-[var(--v-surface-2)] rounded-[var(--v-radius-sm)]">
              <span className="text-[var(--v-text-secondary)]">Monto</span>
              <span className="font-medium">${amountUSD.toFixed(2)} USD</span>
            </div>
            <div className="flex justify-between p-3 bg-[var(--v-surface-2)] rounded-[var(--v-radius-sm)]">
              <span className="text-[var(--v-text-secondary)]">Referencia</span>
              <span className="font-mono font-bold text-[var(--v-teal-700)]">{reference}</span>
            </div>
            {expiresAt && (
              <div className="flex justify-between p-3 bg-[var(--v-surface-2)] rounded-[var(--v-radius-sm)]">
                <span className="text-[var(--v-text-secondary)]">Vence el</span>
                <span className="font-medium">
                  {new Date(expiresAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* OXXO Barcode */}
        {method === 'oxxo' && result.barcode_url && (
          <Card className="p-6 mb-6 text-center">
            <h3 className="font-bold mb-4">Código de barras</h3>
            <img
              src={result.barcode_url}
              alt="Código de barras OXXO"
              className="mx-auto max-w-xs w-full"
            />
            <p className="text-sm text-[var(--v-text-secondary)] mt-4">
              Muestra este código en caja de OXXO para procesar tu pago
            </p>
          </Card>
        )}

        {/* SPEI Instructions */}
        {method === 'spei' && (
          <Card className="p-6 mb-6">
            <h3 className="font-bold mb-4">Instrucciones para SPEI</h3>
            <ol className="space-y-3 text-sm text-[var(--v-text-secondary)]">
              <li className="flex gap-3">
                <span className="font-bold text-[var(--v-teal-700)]">1.</span>
                Ingresa a la app o portal de tu banco
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-[var(--v-teal-700)]">2.</span>
                Selecciona "Transferencia" o "SPEI"
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-[var(--v-teal-700)]">3.</span>
                Captura la referencia: <span className="font-mono font-bold text-[var(--v-text-primary)]">{reference}</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-[var(--v-teal-700)]">4.</span>
                Confirma el monto: <span className="font-bold text-[var(--v-text-primary)]">${amountUSD.toFixed(2)} USD</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-[var(--v-teal-700)]">5.</span>
                Realiza la transferencia y guarda tu comprobante
              </li>
            </ol>
          </Card>
        )}

        <div className="flex items-center justify-center gap-2 text-sm text-[var(--v-text-secondary)]">
          <CheckCircle className="w-4 h-4 text-[var(--v-success)]" />
          Te avisaremos cuando tu pago se confirme
        </div>

        <div className="mt-6 text-center">
          <a href="/billing" className="text-sm text-[var(--v-teal-700)] hover:underline">
            Ir a mi plan
          </a>
        </div>
      </div>
    </div>
  );
}
